import {
  formatProjectDuration,
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  createActionsColumn,
  AbbreviateTableColumn,
  projectTargetGroupMap,
  projectTypeMap,
  MultiLevelDropdownMenu,
  Button,
} from "toptaal-shared"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { Project } from "@/api/types/projects"
import ScreenWrapper from "@/components/ScreenWrapper"
import { TABLE_PAGE_SIZE } from "toptaal-shared"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  useExportMutation,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { projectQueryKeys } from "@/api/query-keys/projects"
import {
  PROJECT_STORAGE_KEY,
  useProjectFilters,
} from "@/hooks/filters/useProjectFilters"
import { z } from "zod"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import ArchiveProjectModal from "@/components/modals/projects/ArchiveProjectModal"
import UnarchiveProjectModal from "@/components/modals/projects/UnarchiveProjectModal"

const ALL_COLUMN_IDS = [
  "id",
  "name",
  "organizations",
  "startDate",
  "type",
  "targetGroup",
]

export const Route = createFileRoute("/beheer/contracten/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "projects"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

function RouteComponent() {
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<Project | null>(null)

  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/contracten/" })
  const navigate = useNavigate({ from: "/beheer/contracten/" })
  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useProjectFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    rowSelection,
    setRowSelection,
    mergedFilters,
    selectedColumns,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...projectQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: PROJECT_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const handleNavigateDetail = (projectId: Project["id"]) => {
    router.navigate({
      to: "/beheer/contracten/$contractId/gegevens",
      params: { contractId: String(projectId) },
      search: { from: router.state.location.href },
    })
  }

  const columns: ColumnDef<Project>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "name",
      accessorKey: "name",
      name: "Contract naam",
      cell: ({ row }) => (
        <span className="block truncate max-w-xs" title={row.original.name}>
          {row.original.name}
        </span>
      ),
    }),
    createCustomColumn({
      id: "organizations",
      name: "Organisaties",
      cell: ({ row }) => (
        <AbbreviateTableColumn entities={row.original.organizations} />
      ),
      accessorKey: "organizations",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "startDate",
      name: "Looptijd",
      cell: ({ row }) => {
        const formattedProjectDuration = formatProjectDuration(
          row.original.startDate,
          row.original.endDate,
        )

        return (
          <div className="flex items-center">
            <div className="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap">
              {formattedProjectDuration}
            </div>
          </div>
        )
      },
      accessorKey: "startDate",
    }),
    createCustomColumn({
      id: "type",
      name: "Type",
      cell: ({ row }) => {
        return <p>{projectTypeMap[row.original.type]}</p>
      },
      accessorKey: "type",
    }),
    createCustomColumn({
      id: "targetGroup",
      name: "Doelgroep",
      cell: ({ row }) => {
        const entities = row.original.targetGroup
          .map((tg) => projectTargetGroupMap[tg])
          .filter(Boolean)
          .map((label) => ({ name: label as string }))

        return <AbbreviateTableColumn entities={entities} />
      },
      accessorKey: "targetGroup",
    }),
    createActionsColumn([
      {
        label: "Contract archiveren",
        onClick: (e, project) => {
          e.stopPropagation()
          setActiveRow(project)
          setIsArchiveOpen(true)
        },
        hidden: archived,
      },
      {
        label: "Contract herstellen",
        onClick: (e, project) => {
          e.stopPropagation()
          setActiveRow(project)
          setIsUnarchiveOpen(true)
        },
        hidden: !archived,
      },
    ]),
  ]

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  const { mutate: exportProject } = useExportMutation({
    exportApiPrefix: "projects",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title={archived ? "Contracten archief" : "Contracten"}
        data={data?.data.projects || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportProject,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuw contract",
                to: "/beheer/contracten/nieuw-contract",
                entity: "projects",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {isArchiveOpen && activeRow && (
        <ArchiveProjectModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          project={activeRow}
        />
      )}

      {isUnarchiveOpen && activeRow && (
        <UnarchiveProjectModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          project={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
